import { create } from "zustand";
import {
  fetchAllDomainsApi,
  fetchDomainCoverageApi,
  fetchDomainControlsApi,
  fetchControlFullApi,
  saveQuestionResponseApi,
  addEvidenceApi,
  removeEvidenceApi,
  updateOwnershipApi,
  addPolicyApi,
  removePolicyApi,
  addRiskMappingApi,
  removeRiskMappingApi,
  addRiskTreatmentApi,
  removeRiskTreatmentApi,
  addRecommendationApi,
  removeRecommendationApi,
  recalculateScoringApi, 
  fetchOverviewApi,
  saveOverviewApi,
fetchReadinessApi, 

} from "./trustApi";

const requireProfile = (profile) => {
  if (!profile || !profile.orgId) {
    throw new Error("Profile or orgId is missing. Please set profile before calling trust APIs.");
  }
};

const useTrustStore = create((set, get) => ({
  // State
  domains: [],
  selectedDomain: null,
  domainControls: [],
  domainCoverage: [],
  selectedControl: null,
  loading: false,
  error: null,
  searchQuery: "",
  domainFilter: "ALL",
  severityFilter: "ALL",
  primaryOnly: true,
  frameworkFilter: "ALL", // NEW: current framework (ISO, SOC2, etc)
  profile: null,
overviewData: null,
overviewLoaded: false,
  applicableFrameworks: [],
  readinessData: [],        
  readinessLoading: false,  
  // Profile
  setProfile: (profile) => set({ profile }),

  // Filters
  setSearchQuery: (q) => set({ searchQuery: q }),
  setDomainFilter: (f) => set({ domainFilter: f }),
  setSeverityFilter: (f) => set({ severityFilter: f }),
  setPrimaryOnly: (v) => set({ primaryOnly: v }),
  setFrameworkFilter: (f) => set({ frameworkFilter: f }), // NEW

  // FETCH ALL DOMAINS
  // fetchDomains: async () => {
  //   set({ loading: true, error: null });
  //   try {
  //     const { primaryOnly, profile } = get();
  //     requireProfile(profile);

  //     const res = await fetchAllDomainsApi(profile, primaryOnly);

  //     set({
  //       domains: res.data?.domains || [],
  //       loading: false,
  //     });
  //   } catch (e) {
  //     set({
  //       error: e.response?.data?.message || e.message || "Failed to fetch domains",
  //       loading: false,
  //     });
  //   }
  // },
  // FETCH FRAMEWORK READINESS
    fetchReadiness: async () => {
    set({ readinessLoading: true });
    try {
      const { profile, applicableFrameworks } = get();
      requireProfile(profile);

      const res = await fetchReadinessApi(profile, applicableFrameworks);
      set({
        readinessData: res.data || {},   // ★ Save ENTIRE response, not just .readiness
        readinessLoading: false,
      });
    } catch (e) {
      console.error("Failed to fetch readiness:", e);
      set({ readinessLoading: false });
    }
  },

  fetchDomains: async () => {
  set({ loading: true, error: null });
  try {
    const { primaryOnly, profile, applicableFrameworks } = get();
    requireProfile(profile);

    const res = await fetchAllDomainsApi(profile, primaryOnly, applicableFrameworks);

    set({
      domains: res.data?.domains || [],
      loading: false,
    });
  } catch (e) {
    set({
      error: e.response?.data?.message || e.message || "Failed to fetch domains",
      loading: false,
    });
  }
},


  // FETCH DOMAIN COVERAGE
  fetchDomainCoverage: async (domainId) => {
    set({ loading: true, error: null });
    try {
      const { primaryOnly, profile } = get();
      requireProfile(profile);

      const res = await fetchDomainCoverageApi(profile, domainId, primaryOnly);

      set({
        selectedDomain: {
          domainId: res.data?.domainId,
          domainName: res.data?.domainName,
          frameworkCount: res.data?.frameworkCount,
        },
        domainCoverage: res.data?.coverage || [],
        loading: false,
      });
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to fetch domain coverage",
        loading: false,
      });
    }
  },

  // FETCH DOMAIN CONTROLS
// FETCH DOMAIN CONTROLS — now uses parallel calls
fetchDomainControls: async (domainId) => {
  set({ loading: true, error: null });
  try {
    const { primaryOnly, profile } = get();
    requireProfile(profile);

    // ── Two lightweight parallel calls instead of one heavy call ──
    const [controlsRes, coverageRes] = await Promise.all([
      fetchDomainControlsApi(profile, domainId, primaryOnly),
      fetchDomainCoverageApi(profile, domainId, primaryOnly),
    ]);

    set({
      selectedDomain: {
        domainId: controlsRes.data?.domainId,
        domainName: controlsRes.data?.domainName,
        controlCount: controlsRes.data?.controlCount,
        frameworksCovered: controlsRes.data?.frameworksCovered,
      },
      domainControls: controlsRes.data?.controls || [],
      domainCoverage: coverageRes.data?.coverage || [],
      loading: false,
    });
  } catch (e) {
    set({
      error: e.response?.data?.message || e.message || "Failed to fetch domain controls",
      loading: false,
    });
  }
},


  // FETCH CONTROL DETAIL
  fetchControlDetail: async (controlId) => {
    set({ loading: true, selectedControl: null, error: null });
    try {
      const { primaryOnly, profile } = get();
      requireProfile(profile);

      const res = await fetchControlFullApi(profile, controlId, primaryOnly);

      set({
        selectedControl: res.data || null,
        loading: false,
      });
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to fetch control detail",
        loading: false,
      });
    }
  },

  // SAVE QUESTION RESPONSE
  saveQuestionResponse: async (
    controlId,
    questionId,
    response,
    notes,
    answeredBy = ""
  ) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await saveQuestionResponseApi(profile, controlId, questionId, {
        response,
        notes,
        answeredBy,
      });

      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to save question response",
      });
    }
  },

  // POLICY
  addPolicy: async (controlId, data) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await addPolicyApi(profile, controlId, data);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to add policy",
      });
    }
  },

  removePolicy: async (controlId, policyId) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await removePolicyApi(profile, controlId, policyId);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to remove policy",
      });
    }
  },

  // EVIDENCE
  addEvidence: async (controlId, data) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await addEvidenceApi(profile, controlId, data);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to add evidence",
      });
    }
  },

  removeEvidence: async (controlId, evidenceId) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await removeEvidenceApi(profile, controlId, evidenceId);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to remove evidence",
      });
    }
  },

  // OWNERSHIP
  updateOwnership: async (controlId, data) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await updateOwnershipApi(profile, controlId, data);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to update ownership",
      });
    }
  },

  // RISK MAPPING
  addRiskMapping: async (controlId, data) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await addRiskMappingApi(profile, controlId, data);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to add risk mapping",
      });
    }
  },

  removeRiskMapping: async (controlId, riskId) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await removeRiskMappingApi(profile, controlId, riskId);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to remove risk mapping",
      });
    }
  },

  // RISK TREATMENT
  addRiskTreatment: async (controlId, data) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await addRiskTreatmentApi(profile, controlId, data);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to add risk treatment",
      });
    }
  },

  removeRiskTreatment: async (controlId, treatmentId) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await removeRiskTreatmentApi(profile, controlId, treatmentId);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to remove risk treatment",
      });
    }
  },

  // RECOMMENDATIONS
  addRecommendation: async (controlId, data) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await addRecommendationApi(profile, controlId, data);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to add recommendation",
      });
    }
  },

  removeRecommendation: async (controlId, recId) => {
    set({ error: null });
    try {
      const { profile } = get();
      requireProfile(profile);

      await removeRecommendationApi(profile, controlId, recId);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to remove recommendation",
      });
    }
  },

  // RECALCULATE SCORING
  recalculateScoring: async (controlId) => {
    set({ error: null });
    try {
      const { primaryOnly, profile } = get();
      requireProfile(profile);

      await recalculateScoringApi(profile, controlId, primaryOnly);
      await get().fetchControlDetail(controlId);
    } catch (e) {
      set({
        error: e.response?.data?.message || e.message || "Failed to recalculate scoring",
      });
    }
  },

  // FILTERED DOMAINS
  getFilteredDomains: () => {
    const { domains, searchQuery, domainFilter } = get();
    let filtered = domains || [];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.domainId?.toLowerCase().includes(q) ||
          d.domainName?.toLowerCase().includes(q) ||
          d.domainGroup?.toLowerCase().includes(q)
      );
    }

    if (domainFilter !== "ALL") {
      filtered = filtered.filter((d) => d.domainId === domainFilter);
    }

    return filtered;
  },

  // FILTERED CONTROLS
  getFilteredControls: () => {
    const { domainControls, searchQuery, severityFilter, frameworkFilter } = get();
    let filtered = domainControls || [];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.controlId?.toLowerCase().includes(q) ||
          c.controlName?.toLowerCase().includes(q) ||
          c.question?.toLowerCase().includes(q) ||
          c.category?.toLowerCase().includes(q)
      );
    }

    if (severityFilter !== "ALL") {
      filtered = filtered.filter((c) => c.severity === severityFilter);
    }

    // NEW: filter controls by selected framework
    if (frameworkFilter && frameworkFilter !== "ALL") {
      filtered = filtered.filter((c) =>
        (c.frameworks || []).some((fw) => fw.name === frameworkFilter)
      );
    }

    return filtered;
  },

//  fetchOverview: async () => {
//   try {
//     const { profile } = get();
//     requireProfile(profile);
//     const res = await fetchOverviewApi(profile);
    
//     if (res.data && res.data.exists !== false && res.data.overviewQuestions) {
//       set({ overviewData: res.data, overviewLoaded: true });
//       return res.data;
//     }
//     set({ overviewData: null, overviewLoaded: true });
//     return null;
//   } catch (e) {
    
//     set({ error: e.message || "Failed to fetch overview", overviewLoaded: true });
//     return null;
//   }
// },


fetchOverview: async () => {
  try {
    const { profile } = get();
    requireProfile(profile);
    const res = await fetchOverviewApi(profile);

    if (res.data && res.data.exists !== false && res.data.overviewQuestions) {
      const complianceAnswer = res.data.overviewQuestions[8]?.answer || "";
      const frameworks = complianceAnswer.split(", ").filter(Boolean);

      set({
        overviewData: res.data,
        overviewLoaded: true,
        applicableFrameworks: frameworks,
      });

      await get().fetchDomains();
       await get().fetchReadiness();
      return res.data;
    }
    set({ overviewData: null, overviewLoaded: true, applicableFrameworks: [] });
    return null;
  } catch (e) {
    set({ error: e.message, overviewLoaded: true });
    return null;
  }
},


saveOverview: async (formData) => {
  try {
    const { profile } = get();
    requireProfile(profile);
    const overviewQuestions = [
      { questionText: "Company name", answer: formData.companyName || "" },
      { questionText: "Industry sector", answer: (formData.businessTypeMulti || []).map(i => i.label).join(", ") },
      { questionText: "Physical locations", answer: formData.locations || "" },
      { questionText: "Countries", answer: (formData.countries || []).map(c => c.label).join(", ") },
      { questionText: "Cities", answer: (formData.cities || []).map(c => c.label).join(", ") },
      { questionText: "Number of users", answer: formData.users || "" },
      { questionText: "Infrastructure type", answer: (formData.infrastructuretypeMulti || []).map(i => i.label).join(", ") },
      { questionText: "Core IT assets", answer: (formData.assetsMulti || []).map(i => i.label).join(", ") },
      { questionText: "Compliance standards", answer: (formData.complianceMulti || []).map(i => i.label).join(", ") },
      { questionText: "Past security incidents", answer: (formData.securityEventMulti || []).map(i => i.label).join(", ") },
      { questionText: "Use of Generative AI", answer: (formData.generativeAIProductMulti || []).map(i => i.label).join(", ") },
      { questionText: "Primary Vendors", answer: Array.isArray(formData.vendor) ? formData.vendor.map(v => v.label).join(", ") : "" },
    ];
    const payload = { companyName: formData.companyName, overviewQuestions };
    await saveOverviewApi(profile, payload);

    // ★ UPDATED — set frameworks and re-fetch domains
    const frameworks = (formData.complianceMulti || []).map((i) => i.label);
    set({
      overviewData: payload,
      overviewLoaded: true,
      applicableFrameworks: frameworks,
    });
    await get().fetchDomains();
    await get().fetchReadiness();
 
    return true;
  } catch (e) {
    set({ error: e.message || "Failed to save overview" });
    return false;
  }
},

  // OPTIONAL RESET
  resetTrustState: () =>
    set({
      domains: [],
      selectedDomain: null,
      domainControls: [],
      domainCoverage: [],
      selectedControl: null,
      loading: false,
      error: null,
      searchQuery: "",
      domainFilter: "ALL",
      severityFilter: "ALL",
      primaryOnly: true,
      frameworkFilter: "ALL",
      readinessData: [],        
      readinessLoading: false,  
    }),
}));

export default useTrustStore;
